import React, { useEffect, useState } from 'react';
import {
  Check,
  ChevronDown,
  Download,
  ShieldCheck,
  Smartphone,
  Sparkles,
  X,
  Zap,
} from 'lucide-react';
import { APK_RELEASE, apkDownloadUrl } from '../../config/apkRelease';
import loaderIcon from '../../static/images/dq_logo.png';

const ApkLaunchModal = ({ open, onClose }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [downloaded, setDownloaded] = useState(false);
  const [entering, setEntering] = useState(false);

  useEffect(() => {
    if (!open) return undefined;

    setDownloaded(false);
    setActiveStep(0);
    requestAnimationFrame(() => setEntering(true));

    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKeyDown);
      setEntering(false);
    };
  }, [open, onClose]);

  if (!open || !APK_RELEASE.enabled) return null;

  const handleDownload = () => {
    setDownloaded(true);
    setActiveStep(1);
  };

  return (
    <div
      className={`
        fixed inset-0 z-[60] flex items-end sm:items-center justify-center
        p-0 sm:p-4 lg:p-6
        transition-all duration-300
        ${entering ? 'bg-black/55 backdrop-blur-md' : 'bg-black/0'}
      `}
      onClick={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="apk-modal-title"
        onClick={(e) => e.stopPropagation()}
        className={`
          relative flex flex-col w-full max-w-5xl min-w-0
          max-h-[92dvh] sm:max-h-[90vh]
          overflow-hidden bg-white
          rounded-t-2xl sm:rounded-2xl
          shadow-[0_30px_80px_rgba(76,29,149,0.35)]
          pb-[env(safe-area-inset-bottom)]
          transition-all duration-500 ease-out
          ${entering ? 'opacity-100 translate-y-0 sm:scale-100' : 'opacity-0 translate-y-8 sm:translate-y-6 sm:scale-[0.97]'}
        `}
      >
        {/* Ambient glow */}
        <div className="pointer-events-none absolute -top-24 -right-20 h-72 w-72 rounded-full bg-fuchsia-400/30 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-28 -left-16 hidden sm:block h-72 w-72 rounded-full bg-indigo-400/25 blur-3xl" />

        <button
          type="button"
          onClick={onClose}
          className="
            absolute top-3 right-3 sm:top-4 sm:right-4 z-20
            flex h-10 w-10 items-center justify-center rounded-full
            bg-black/20 text-white backdrop-blur
            border border-white/25
            hover:bg-black/30 transition
            lg:bg-gray-100 lg:text-gray-700 lg:border-gray-200 lg:hover:bg-gray-200
            touch-manipulation
          "
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="relative flex-1 min-h-0 flex flex-col lg:grid lg:grid-cols-5">
          {/* LEFT — visual / hero */}
          <div
            className="
              relative lg:col-span-2 shrink-0
              bg-gradient-to-br from-pink-600 via-purple-700 to-indigo-800
              text-white px-4 pt-3 pb-5 sm:px-8 sm:py-10
              flex flex-col justify-between gap-4 sm:gap-8
              overflow-hidden
            "
          >
            <div className="absolute inset-0 opacity-20"
              style={{
                backgroundImage:
                  'radial-gradient(circle at 20% 20%, white 0.8px, transparent 1px), radial-gradient(circle at 80% 60%, white 0.8px, transparent 1px)',
                backgroundSize: '28px 28px',
              }}
            />

            <div className="relative flex sm:hidden justify-center pb-2">
              <div className="w-12 h-1.5 bg-white/40 rounded-full" />
            </div>

            <div className="relative space-y-3 sm:space-y-5">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1.5 text-[10px] sm:text-xs font-semibold uppercase tracking-wider backdrop-blur">
                <Sparkles className="w-3.5 h-3.5" />
                New launch
              </div>

              <div className="flex items-start gap-3 sm:gap-4 pr-10">
                <div className="relative shrink-0">
                  <div className="absolute inset-0 rounded-2xl bg-white/30 blur-md animate-pulse" />
                  <div className="relative h-14 w-14 sm:h-24 sm:w-24 rounded-xl sm:rounded-2xl overflow-hidden border border-white/30 shadow-xl bg-black">
                    <img
                      src={loaderIcon}
                      alt="DigiQin"
                      className="h-full w-full object-cover"
                    />
                  </div>
                </div>
                <div className="pt-0.5 sm:pt-1 min-w-0">
                  <h2 id="apk-modal-title" className="text-xl sm:text-3xl font-bold leading-tight">
                    {APK_RELEASE.title}
                  </h2>
                  {APK_RELEASE.version && (
                    <p className="mt-1.5 sm:mt-2 inline-flex flex-wrap items-center gap-2 text-xs sm:text-sm text-white/85">
                      <span className="rounded-md bg-white/15 px-2 py-0.5 font-semibold">
                        v{APK_RELEASE.version}
                      </span>
                      <span className="text-white/70">{APK_RELEASE.fileSizeLabel}</span>
                    </p>
                  )}
                </div>
              </div>

              <p className="text-sm sm:text-base text-white/85 leading-relaxed max-w-md">
                {APK_RELEASE.subtitle}
              </p>

              <div className="flex flex-wrap gap-1.5 sm:gap-2">
                {APK_RELEASE.platforms.map((chip) => (
                  <span
                    key={chip}
                    className="inline-flex items-center gap-1.5 rounded-full border border-white/25 bg-white/10 px-2.5 sm:px-3 py-1 text-[11px] sm:text-xs font-medium backdrop-blur"
                  >
                    <Zap className="w-3 h-3" />
                    {chip}
                  </span>
                ))}
              </div>
            </div>

            <div className="relative mt-2 hidden lg:block">
              <div
                className="
                  mx-auto lg:mx-0
                  w-[180px] sm:w-[200px]
                  rounded-[1.75rem] border-[6px] border-white/25
                  bg-gradient-to-b from-white/15 to-white/5
                  p-3 shadow-2xl backdrop-blur
                  animate-float
                "
              >
                <div className="rounded-2xl bg-black/40 aspect-[9/16] flex flex-col items-center justify-center gap-3 p-4">
                  <Smartphone className="w-10 h-10 text-white/90" />
                  <p className="text-center text-xs font-semibold text-white/90 leading-snug">
                    DigiQin on Android
                  </p>
                  <div className="h-1.5 w-16 rounded-full bg-white/30 overflow-hidden">
                    <div className="h-full w-2/3 rounded-full bg-gradient-to-r from-pink-300 to-indigo-300 animate-pulse" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT — interactive content */}
          <div className="relative lg:col-span-3 bg-white flex flex-col min-h-0 min-w-0 flex-1">
            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-4 sm:px-8 sm:py-9 flex flex-col gap-5 sm:gap-7">
              <div>
                <div className="flex items-center justify-between gap-3 mb-3 sm:mb-4">
                  <div>
                    <p className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.2em] text-purple-700">
                      What&apos;s new
                    </p>
                    <h3 className="text-lg sm:text-2xl font-semibold text-gray-900 mt-0.5 sm:mt-1">
                      Release updates
                    </h3>
                  </div>
                  <span className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-700 px-3 py-1 text-xs font-semibold border border-emerald-100">
                    <ShieldCheck className="w-3.5 h-3.5" />
                    Direct download
                  </span>
                </div>

                <ul className="space-y-2 sm:space-y-3">
                  {APK_RELEASE.updates.map((item, idx) => (
                    <li
                      key={idx}
                      className="
                        group flex gap-3 rounded-xl border border-purple-100
                        bg-gradient-to-r from-purple-50/80 via-white to-indigo-50/50
                        px-3 py-2.5 sm:px-4 sm:py-3.5
                        transition-all duration-300
                        hover:border-purple-300 hover:shadow-md hover:-translate-y-0.5
                      "
                      style={{ animationDelay: `${idx * 80}ms` }}
                    >
                      <span className="mt-0.5 flex h-6 w-6 sm:h-7 sm:w-7 shrink-0 items-center justify-center rounded-full bg-purple-600 text-white shadow-sm group-hover:scale-110 transition">
                        <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4" strokeWidth={2.5} />
                      </span>
                      <span className="text-sm sm:text-base text-gray-700 leading-relaxed pt-0.5 min-w-0">
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <p className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.2em] text-purple-700 mb-2.5 sm:mb-3">
                  How to install
                </p>
                <div className="space-y-2">
                  {APK_RELEASE.installSteps.map((step, idx) => {
                    const isOpen = activeStep === idx;
                    const isDone = downloaded && idx === 0;
                    return (
                      <button
                        key={step.title}
                        type="button"
                        onClick={() => setActiveStep(isOpen ? -1 : idx)}
                        className={`
                          w-full text-left rounded-xl border px-3 py-3 sm:px-4 sm:py-3.5
                          transition-all duration-300 touch-manipulation
                          ${isOpen
                            ? 'border-purple-400 bg-purple-50 shadow-sm'
                            : 'border-gray-200 bg-white hover:border-purple-200 hover:bg-purple-50/40'}
                        `}
                      >
                        <div className="flex items-center gap-3">
                          <span
                            className={`
                              flex h-7 w-7 sm:h-8 sm:w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold
                              ${isDone
                                ? 'bg-emerald-500 text-white'
                                : isOpen
                                  ? 'bg-purple-700 text-white'
                                  : 'bg-gray-100 text-gray-600'}
                            `}
                          >
                            {isDone ? <Check className="w-4 h-4" /> : idx + 1}
                          </span>
                          <span className="flex-1 min-w-0 font-semibold text-gray-900 text-sm sm:text-base">
                            {step.title}
                          </span>
                          <ChevronDown
                            className={`w-4 h-4 shrink-0 text-gray-500 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
                          />
                        </div>
                        <div
                          className={`
                            overflow-hidden transition-all duration-300
                            ${isOpen ? 'max-h-40 opacity-100 mt-2' : 'max-h-0 opacity-0'}
                          `}
                        >
                          <p className="pl-10 sm:pl-11 text-sm text-gray-600 leading-relaxed">
                            {step.detail}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="shrink-0 border-t border-gray-100 bg-white px-4 py-3 sm:px-8 sm:py-4">
              {downloaded && (
                <p className="mb-3 text-xs sm:text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2 flex items-start sm:items-center gap-2">
                  <Check className="w-4 h-4 shrink-0 mt-0.5 sm:mt-0" />
                  Download started — open the file from your Downloads folder to install.
                </p>
              )}

              <div className="flex flex-col-reverse sm:flex-row gap-2.5 sm:gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="
                    px-5 py-3 sm:py-3.5 rounded-xl
                    border border-gray-200 bg-white
                    text-gray-700 text-sm font-semibold
                    hover:bg-gray-50 transition
                    touch-manipulation
                  "
                >
                  Maybe later
                </button>

                <a
                  href={apkDownloadUrl()}
                  onClick={handleDownload}
                  className="
                    group relative flex-1
                    inline-flex items-center justify-center gap-3
                    px-6 py-3 sm:py-3.5 rounded-xl
                    text-white text-sm sm:text-base font-semibold
                    bg-gradient-to-r from-pink-600 via-purple-700 to-indigo-700
                    shadow-[0_14px_35px_rgba(124,58,237,0.4)]
                    transition-all duration-300
                    hover:shadow-[0_18px_40px_rgba(124,58,237,0.5)]
                    hover:-translate-y-0.5
                    active:translate-y-0
                    overflow-hidden
                    touch-manipulation
                  "
                >
                  <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                  <Download className="relative w-5 h-5 group-hover:animate-bounce" />
                  <span className="relative">
                    {downloaded ? 'Download again' : 'Download APK'}
                  </span>
                </a>
              </div>

              <p className="mt-2.5 sm:mt-3 text-center sm:text-left text-[11px] sm:text-xs text-gray-400">
                File: <span className="font-medium text-gray-500">{APK_RELEASE.fileName}</span>
                {' · '}
                No Play Store required · Download Directly from here.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApkLaunchModal;
